import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Pencil, Plus, Send, Shield, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, ErrorState, IconButton, LoadingState, useConfirm } from '@/components/primitives';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { adminApi, formatDate, inferIocType, IOC_TYPES, iocProblem, iocTypeLabel, plural, toastError, useAdminQuery, useBusy } from './adminApi';
import { CampaignStatusBadge, FilterChips, IocStatusBadge, IocTypeBadge, IocValue, Pager, RefreshError, SearchField, sourceLabel, Toolbar } from './AdminUi';

const PAGE = 50;

const suspect = (iocs) => iocs.filter((i) => iocProblem(i.type, i.value)).length;

const typeSummary = (iocs) => {
  const counts = {};
  iocs.forEach((i) => { counts[i.type] = (counts[i.type] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([t, n]) => `${n} ${iocTypeLabel(t)}`).join(', ');
};

/**
 * One campaign: moderation bar (publish / reject with confirmation), the
 * description, an add-IOC form and the full IOC table (values never cut).
 */
export default function CampaignDetail({ campaign, onChanged, onEdit, onDeleted }) {
  const iocsQuery = useAdminQuery(
    () => adminApi.get('/admin/threat-hunt-iocs', { params: { campaign_id: campaign.id } }).then((r) => r.data.iocs || []),
    [campaign.id]
  );
  const iocs = iocsQuery.data || [];
  const { run, busy } = useBusy();
  const [confirm, confirmDialog] = useConfirm();

  const [q, setQ] = useState('');
  const [type, setType] = useState(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => { setQ(''); setType(null); setOffset(0); }, [campaign.id]);

  const pendingIocs = iocs.filter((i) => i.status !== 'approved');
  const typeCounts = useMemo(() => {
    const c = {};
    iocs.forEach((i) => { c[i.type] = (c[i.type] || 0) + 1; });
    return c;
  }, [iocs]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return iocs.filter((i) =>
      (!type || i.type === type) &&
      (!needle || String(i.value).toLowerCase().includes(needle) || String(i.description || '').toLowerCase().includes(needle))
    );
  }, [iocs, q, type]);
  const page = filtered.slice(offset, offset + PAGE);

  // Imports stamp every IOC with the same description; show it once instead of on every row.
  const sharedDescription = useMemo(() => {
    if (iocs.length < 2) return null;
    const first = iocs[0].description || '';
    return iocs.every((i) => (i.description || '') === first) ? first : null;
  }, [iocs]);
  const showDescription = sharedDescription === null;

  const refreshAll = () => {
    iocsQuery.reload();
    onChanged?.();
  };

  /* ── Moderation ─────────────────────────────────────────── */

  const publish = async () => {
    const toPublish = campaign.status === 'approved' ? pendingIocs : iocs;
    const ok = await confirm({
      title: campaign.status === 'approved' ? `Publish ${plural(toPublish.length, 'pending IOC')}?` : `Publish "${campaign.name}"?`,
      description: campaign.status === 'approved'
        ? 'They become visible immediately to every customer that can see this campaign.'
        : 'The campaign and its IOCs become visible to every customer immediately.',
      body: (
        <ul className="adm-list-bullets">
          <li>{plural(toPublish.length, 'IOC')}{toPublish.length ? `: ${typeSummary(toPublish)}` : ''}</li>
          {suspect(toPublish) > 0 && <li>{plural(suspect(toPublish), 'value')} {suspect(toPublish) === 1 ? 'does' : 'do'} not look like {suspect(toPublish) === 1 ? 'its' : 'their'} type; review before publishing.</li>}
          <li>Audience: all customers</li>
        </ul>
      ),
      confirmLabel: campaign.status === 'approved' ? `Publish ${plural(toPublish.length, 'IOC')}` : 'Publish to all customers',
    });
    if (!ok) return;
    run('publish', async () => {
      try {
        const res = await adminApi.post(`/admin/threat-hunt-campaigns/${campaign.id}/approve`);
        toast.success(res.data?.message || 'Campaign published');
        refreshAll();
      } catch (err) {
        toastError(err, 'Could not publish the campaign');
      }
    });
  };

  const reject = async () => {
    const live = campaign.status === 'approved';
    const ok = await confirm({
      title: live ? `Unpublish "${campaign.name}"?` : `Reject "${campaign.name}"?`,
      description: live
        ? 'Customers stop seeing this campaign and its IOCs right away. You can publish it again later.'
        : 'It stays hidden from customers and moves to Rejected. You can still publish it later.',
      confirmLabel: live ? 'Unpublish' : 'Reject campaign',
      destructive: true,
    });
    if (!ok) return;
    run('reject', async () => {
      try {
        await adminApi.post(`/admin/threat-hunt-campaigns/${campaign.id}/reject`);
        toast.success(live ? 'Campaign unpublished' : 'Campaign rejected');
        refreshAll();
      } catch (err) {
        toastError(err, 'Could not reject the campaign');
      }
    });
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete "${campaign.name}"?`,
      description: `The campaign and its ${plural(campaign.ioc_count ?? iocs.length, 'IOC')} are deleted permanently${campaign.status === 'approved' ? ', and customers lose access to them' : ''}.`,
      confirmLabel: 'Delete campaign',
      destructive: true,
    });
    if (!ok) return;
    run('delete', async () => {
      try {
        await adminApi.delete(`/admin/threat-hunt-campaigns/${campaign.id}`);
        toast.success('Campaign deleted');
        onDeleted?.(campaign.id);
      } catch (err) {
        toastError(err, 'Could not delete the campaign');
      }
    });
  };

  /* ── IOCs ───────────────────────────────────────────────── */

  const [newIoc, setNewIoc] = useState({ type: 'ip', value: '', description: '', typeTouched: false });
  useEffect(() => { setNewIoc({ type: 'ip', value: '', description: '', typeTouched: false }); }, [campaign.id]);

  const onValueChange = (value) => {
    setNewIoc((s) => {
      const guess = s.typeTouched ? null : inferIocType(value);
      return { ...s, value, type: guess || s.type };
    });
  };

  const addIoc = (e) => {
    e.preventDefault();
    const value = newIoc.value.trim();
    if (!value) {
      toast.error('Enter an IOC value');
      return;
    }
    run('add', async () => {
      try {
        await adminApi.post('/admin/threat-hunt-iocs', {
          campaign_id: campaign.id,
          type: newIoc.type,
          value,
          description: newIoc.description.trim(),
          source: '',
        });
        toast.success('IOC added as pending', {
          description: campaign.status === 'approved' ? 'Publish it to make it visible to customers.' : 'It is published together with the campaign.',
        });
        setNewIoc((s) => ({ ...s, value: '', description: '', typeTouched: false }));
        refreshAll();
      } catch (err) {
        toastError(err, 'Could not add the IOC');
      }
    });
  };

  const deleteIoc = async (ioc) => {
    const ok = await confirm({
      title: 'Delete this IOC?',
      description: ioc.status === 'approved' ? 'It is published: customers stop seeing it immediately.' : 'It has not been published yet.',
      body: <code className="adm-ioc-value">{ioc.value}</code>,
      confirmLabel: 'Delete IOC',
      destructive: true,
    });
    if (!ok) return;
    run(`del-${ioc.id}`, async () => {
      try {
        await adminApi.delete(`/admin/threat-hunt-iocs/${ioc.id}`);
        iocsQuery.setData((prev) => (prev || []).filter((i) => i.id !== ioc.id));
        toast.success('IOC deleted');
        onChanged?.();
      } catch (err) {
        toastError(err, 'Could not delete the IOC');
      }
    });
  };

  const publishIoc = async (ioc) => {
    const ok = await confirm({
      title: 'Publish this IOC?',
      description: 'Customers who can see this live campaign see it immediately.',
      body: <code className="adm-ioc-value">{ioc.value}</code>,
      confirmLabel: 'Publish IOC',
    });
    if (!ok) return;
    run(`pub-${ioc.id}`, async () => {
      try {
        await adminApi.post(`/admin/threat-hunt-iocs/${ioc.id}/approve`);
        iocsQuery.setData((prev) => (prev || []).map((i) => (i.id === ioc.id ? { ...i, status: 'approved' } : i)));
        toast.success('IOC published');
        onChanged?.();
      } catch (err) {
        toastError(err, 'Could not publish the IOC');
      }
    });
  };

  /* ── Render ─────────────────────────────────────────────── */

  const live = campaign.status === 'approved';
  const typeOptions = [
    { value: null, label: 'All types', count: iocs.length },
    ...IOC_TYPES.filter((t) => typeCounts[t.value]).map((t) => ({ value: t.value, label: t.label, count: typeCounts[t.value] })),
  ];

  return (
    <div className="adm-detail" data-testid="campaign-detail">
      <header className="adm-detail-head">
        <div className="adm-detail-titles">
          <h3 className="adm-detail-title">{campaign.name}</h3>
          <div className="adm-detail-meta">
            <CampaignStatusBadge status={campaign.status} size="md" />
            <Badge plain size="sm">{sourceLabel(campaign.source_type)}</Badge>
            <span className="adm-meta">Created {formatDate(campaign.created_at)}</span>
            {campaign.reviewed_at && <span className="adm-meta">Reviewed {formatDate(campaign.reviewed_at)}</span>}
            {campaign.source_url && (
              <a href={campaign.source_url} target="_blank" rel="noopener noreferrer" className="adm-link">
                <ExternalLink aria-hidden="true" /> Source<span className="sr-only"> (opens in a new tab)</span>
              </a>
            )}
          </div>
        </div>
        <div className="adm-detail-actions">
          <Button variant="outline" size="sm" onClick={() => onEdit(campaign)}>
            <Pencil aria-hidden="true" /> Edit
          </Button>
          <IconButton
            label={`Delete campaign ${campaign.name}`}
            icon={busy('delete') ? Loader2 : Trash2}
            variant="danger"
            onClick={remove}
            disabled={busy('delete')}
            className={busy('delete') ? 'adm-spin' : undefined}
          />
        </div>
      </header>

      <div className={`adm-moderation adm-moderation--${campaign.status}`} role="status">
        <span className="adm-moderation-text">
          {campaign.status === 'pending' && <><Shield aria-hidden="true" /> Draft: customers cannot see this campaign until you publish it.</>}
          {campaign.status === 'rejected' && <><XCircle aria-hidden="true" /> Rejected: hidden from customers.</>}
          {live && pendingIocs.length > 0 && <><AlertTriangle aria-hidden="true" /> Live, but {plural(pendingIocs.length, 'IOC')} added since publishing {pendingIocs.length === 1 ? 'is' : 'are'} not visible to customers yet.</>}
          {live && pendingIocs.length === 0 && <><CheckCircle2 aria-hidden="true" /> Live: visible to customers.</>}
        </span>
        <span className="adm-moderation-actions">
          {campaign.status !== 'rejected' && (
            <Button variant="outline" size="sm" onClick={reject} disabled={busy('reject')}>
              {busy('reject') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <XCircle aria-hidden="true" />}
              {live ? 'Unpublish' : 'Reject'}
            </Button>
          )}
          {(!live || pendingIocs.length > 0) && (
            <Button size="sm" onClick={publish} disabled={busy('publish') || iocsQuery.loading} data-testid="publish-campaign-btn">
              {busy('publish') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Send aria-hidden="true" />}
              {busy('publish') ? 'Publishing…' : live ? `Publish ${plural(pendingIocs.length, 'IOC')}` : 'Approve & publish'}
            </Button>
          )}
        </span>
      </div>

      {campaign.description && (
        <details className="adm-description">
          <summary>Description</summary>
          <div className="adm-description-body">
            <MarkdownRenderer content={campaign.description} />
          </div>
        </details>
      )}

      <form className="adm-add-ioc" onSubmit={addIoc} aria-label="Add an IOC to this campaign">
        <div className="adm-field adm-add-ioc-type">
          <label htmlFor={`ioc-type-${campaign.id}`} className="adm-label">Type</label>
          <select
            id={`ioc-type-${campaign.id}`}
            className="adm-input adm-select"
            value={newIoc.type}
            onChange={(e) => setNewIoc({ ...newIoc, type: e.target.value, typeTouched: true })}
          >
            {IOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="adm-field adm-add-ioc-value">
          <label htmlFor={`ioc-value-${campaign.id}`} className="adm-label">Value</label>
          <input
            id={`ioc-value-${campaign.id}`}
            className="adm-input adm-mono"
            value={newIoc.value}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder="203.0.113.10, evil.example, https://…, sha256…"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="adm-field adm-add-ioc-desc">
          <label htmlFor={`ioc-desc-${campaign.id}`} className="adm-label">Description (optional)</label>
          <input
            id={`ioc-desc-${campaign.id}`}
            className="adm-input"
            value={newIoc.description}
            onChange={(e) => setNewIoc({ ...newIoc, description: e.target.value })}
          />
        </div>
        <Button type="submit" size="sm" disabled={busy('add')} className="adm-add-ioc-submit">
          {busy('add') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Plus aria-hidden="true" />}
          {busy('add') ? 'Adding…' : 'Add IOC'}
        </Button>
      </form>

      <div className="adm-subhead">
        <h4 className="adm-subhead-title">
          IOCs <span className="adm-section-count">{iocsQuery.data ? iocs.length : '…'}</span>
        </h4>
        {iocs.length > 0 && (
          <span className="adm-meta">
            {plural(iocs.length - pendingIocs.length, 'published IOC', 'published')} · {pendingIocs.length} pending
          </span>
        )}
      </div>

      {iocsQuery.loading ? (
        <LoadingState label="IOCs" rows={4} />
      ) : iocsQuery.error && !iocsQuery.data ? (
        <ErrorState title="Couldn't load this campaign's IOCs" error={iocsQuery.error} onRetry={iocsQuery.reload} />
      ) : !iocs.length ? (
        <EmptyState variant="compact" title="No IOCs yet" description="Add indicators above, or approve auto-collected IOCs into this campaign." />
      ) : (
        <>
          <RefreshError error={iocsQuery.error} onRetry={iocsQuery.reload} what="IOCs" />
          <Toolbar>
            <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search IOCs in this campaign" placeholder="Search value or description" />
            <FilterChips label="IOC type" options={typeOptions} value={type} onChange={(v) => { setType(v); setOffset(0); }} />
          </Toolbar>
          {sharedDescription && <p className="adm-shared-desc"><span className="adm-label">Every IOC</span> {sharedDescription}</p>}
          {!filtered.length ? (
            <EmptyState variant="compact" title="No matching IOCs" description="Change the search or the type filter." />
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table adm-table--stack" data-testid="campaign-iocs-table">
                <thead>
                  <tr>
                    <th scope="col" className="adm-col-type">Type</th>
                    <th scope="col" className="adm-col-value">Value</th>
                    <th scope="col">Status</th>
                    {showDescription && <th scope="col">Description</th>}
                    <th scope="col" className="adm-col-actions"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {page.map((ioc) => (
                    <tr key={ioc.id}>
                      <td data-label="Type"><IocTypeBadge type={ioc.type} /></td>
                      <td data-label="Value" className="adm-col-value"><IocValue value={ioc.value} type={ioc.type} /></td>
                      <td data-label="Status"><IocStatusBadge status={ioc.status} /></td>
                      {showDescription && <td data-label="Description" className="adm-secondary adm-break">{ioc.description || '-'}</td>}
                      <td className="adm-actions-cell">
                        {live && ioc.status !== 'approved' && (
                          <IconButton
                            label={`Publish ${ioc.value}`}
                            icon={busy(`pub-${ioc.id}`) ? Loader2 : Send}
                            disabled={busy(`pub-${ioc.id}`)}
                            className={busy(`pub-${ioc.id}`) ? 'adm-spin' : undefined}
                            onClick={() => publishIoc(ioc)}
                          />
                        )}
                        <IconButton
                          label={`Delete ${ioc.value}`}
                          icon={busy(`del-${ioc.id}`) ? Loader2 : Trash2}
                          variant="danger"
                          disabled={busy(`del-${ioc.id}`)}
                          className={busy(`del-${ioc.id}`) ? 'adm-spin' : undefined}
                          onClick={() => deleteIoc(ioc)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager offset={offset} limit={PAGE} total={filtered.length} onChange={setOffset} label="IOC pages" />
        </>
      )}
      {confirmDialog}
    </div>
  );
}
